import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { environment } from '../../../../environments/environment';
import {
  from,
  Observable,
  forkJoin,
  map,
  switchMap,
  catchError,
  of,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  profile: any;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private http: HttpClient
  ) {}

  getUserDetails() {
    try {
      const profileData = this.msalService.instance.getAllAccounts()[0];
      this.profile = { name: profileData.name, mail: profileData.username };
      return this.profile;
    } catch {
      return {};
    }
  }

  microsoftLogin() {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  microsoftLogout() {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: location.origin,
    });
  }

  getTokenFromDRF() {
    let refreshToken = {};
    return this.http.post(environment.drfBackendBaseUrl, refreshToken);
  }

  /** Acquire Microsoft Graph token as Observable */
  getGraphTokenObservable(): Observable<AuthenticationResult | null> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) return of(null);

    return from(
      this.msalService.acquireTokenSilent({
        account,
        scopes: ['User.Read', 'Organization.Read.All'],
      })
    ).pipe(
      catchError(() => {
        return of(null);
      })
    );
  }

  loadProfileAndOrg(): Observable<any> {
    return this.getGraphTokenObservable().pipe(
      switchMap((tokenResponse) => {
        if (!tokenResponse) return of(null);

        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        };

        return forkJoin({
          user: this.http.get(
            'https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,mobilePhone,userPrincipalName',
            { headers }
          ),
          org: this.http.get(
            'https://graph.microsoft.com/v1.0/organization?$select=displayName,street,city,state,postalCode,country,businessPhones,id',
            { headers }
          ),
        }).pipe(
          map(({ user, org }: any) => {
            const orgData = org?.value?.[0] || {};

            const fullAddress = [
              orgData.street,
              orgData.city,
              orgData.state,
              orgData.postalCode,
              orgData.country,
            ]
              .filter(Boolean)
              .join(', ');

            return {
              name: user.displayName,
              mail: user.mail || user.userPrincipalName,
              mobile: user.mobilePhone || '',
              organization: orgData.displayName || '',
              businessPhones: orgData.businessPhones || [],
              fullAddress: fullAddress,
              org_tenant_id: orgData.id || '',
              user_tenant_id: user.id,
            };
          }),
          catchError(() => {
            return of(null);
          })
        );
      })
    );
  }
}
